/**
 * Umami analytics (self-hosted, cookieless).
 *
 * The script is injected by the root layout and auto-tracks page views,
 * including App Router client-side navigations (it patches history.pushState).
 *
 * Everything here is client-side only and guards on `window.umami`, so it stays
 * a no-op in development, on the server, or when the script is blocked.
 */

type UmamiValue = string | number | boolean | null | undefined

export type UmamiEventData = Record<string, UmamiValue>

type UmamiPayload = Record<string, unknown>

type UmamiApi = {
  track: (
    eventNameOrPayload?: string | UmamiPayload | ((props: UmamiPayload) => UmamiPayload),
    eventData?: UmamiPayload
  ) => void
  identify: (
    idOrData: string | Record<string, unknown>,
    data?: Record<string, unknown>
  ) => void
}

const getUmami = (): UmamiApi | null => {
  if (typeof window === 'undefined') return null
  const umami = (window as unknown as { umami?: UmamiApi }).umami
  return typeof umami?.track === 'function' ? umami : null
}

export const isUmamiEnabled = () => getUmami() !== null

/**
 * Umami only accepts flat scalar properties, and drops the whole event when a
 * value is an object. Anything else is stringified, empty values are dropped.
 */
const sanitize = (data?: UmamiEventData): Record<string, string | number | boolean> => {
  const clean: Record<string, string | number | boolean> = {}

  if (!data) return clean

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined || value === '') continue
    clean[key] =
      typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
        ? value
        : String(value)
  }

  return clean
}

/**
 * Track a custom event. Event names are kept identical to the GA ones so both
 * dashboards can be compared side by side.
 */
export const umamiTrack = (name: string, data?: UmamiEventData) => {
  const umami = getUmami()
  if (!umami) return

  try {
    umami.track(name, sanitize(data))
  } catch {
    // Analytics must never break the page
  }
}

/**
 * Track a page view manually (the script already does it on navigation, this is
 * only needed for virtual steps such as a multi-step form).
 */
export const umamiPageView = (url: string, title?: string) => {
  const umami = getUmami()
  if (!umami) return

  try {
    umami.track((props) => ({
      ...props,
      url,
      ...(title ? { title } : {}),
    }))
  } catch {
    // Analytics must never break the page
  }
}

export interface UmamiVisitor {
  /** Authenticated user id, absent for anonymous visitors. */
  userId?: string | number
  /** Numeric role from the backend, absent for anonymous visitors. */
  role?: number | string
  locale?: string
  /** Human readable role name, used for segmentation in the dashboard. */
  roleName?: string
}

/**
 * Attach session data to the current Umami session so visitor and member
 * journeys can be segmented. Called on every page load by <UmamiIdentity />.
 */
export const umamiIdentify = (visitor: UmamiVisitor) => {
  const umami = getUmami()
  if (!umami || typeof umami.identify !== 'function') return

  const isMember = visitor.userId !== undefined && visitor.userId !== null

  const data = sanitize({
    audience: isMember ? 'member' : 'visitor',
    role: visitor.role,
    roleName: visitor.roleName,
    locale: visitor.locale,
  })

  try {
    if (isMember) {
      umami.identify(String(visitor.userId), data)
    } else {
      umami.identify(data)
    }
  } catch {
    // Analytics must never break the page
  }
}
